import { LatLngExpression } from 'leaflet'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LayerGroup, Polygon } from 'react-leaflet'
import { useParams } from 'react-router'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useNavigation } from 'src/context/NavigationContext'
import { ICluster } from 'src/generated-types/cluster'
import { IProjectInfo } from 'src/generated-types/project'
import { hasRenderableGeometry } from 'src/utils/position-geometry'
import { AppRoutesNavigation } from '../../../../app-routes'
import { ILocality, IMooring, IMooringType, IPosition, IPositionType } from '../../../../generated-types'
import { CageLabelFontSizeProvider } from './CageLabelFontSizeContext'
import { Bridle } from './components/Bridle'
import { Cage } from './components/Cage'
import { Corner } from './components/Corner'
import { Fleet } from './components/Fleet'
import { FrameLine } from './components/FrameLine'
import { GeometryPosition } from './components/GeometryPosition'
import { InnerMooringLine } from './components/InnerMooringLine'
import { MooringLine } from './components/MooringLine'
import { defaultRadiusSize } from './constraints'
import { CreateMooring } from './layers/CreateMooring'
import { CreateProject } from './layers/CreateProject'
import { useMooringsCtx } from 'src/hooks/context-hooks'

export const GenericComponentLayer: React.FC<{
  position: IPosition
  isFleet?: boolean
  steelFrame: boolean
  navigate?: NavigateFunction
  showCageStatusPopOver?: boolean
  onPositionSelect?: (positionId: number, positionName: string) => void
  mooringType?: IMooringType
  showCriticalPositions?: boolean
  isSmallMapView?: boolean
}> = ({
  position,
  isFleet = false,
  steelFrame,
  navigate,
  showCageStatusPopOver,
  onPositionSelect,
  mooringType,
  showCriticalPositions,
  isSmallMapView,
}) => {
  const { customerId, id, localityId, mooringId } = useParams()
  const [localityIdFromQuery, setLocalityIdFromQuery] = useState<string | null>()
  const { navigateTo } = useNavigation()
  const { isCritical } = useMooringsCtx()
  const shouldHighlightCritical = !!(isCritical || showCriticalPositions)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const localityId = params.get('localityId')
    setLocalityIdFromQuery(localityId)
  }, [])

  const navigateToPosition = useCallback(
    (positionId: number) => {
      if (onPositionSelect) {
        onPositionSelect(positionId, position.name)
      } else if (
        (customerId || id) &&
        (localityId || localityIdFromQuery != null) &&
        (mooringId || position.mooringId) &&
        positionId
      ) {
        navigate &&
          navigateTo(
            AppRoutesNavigation.Localities.position(
              (customerId ?? id) as EntityId,
              (localityId ?? localityIdFromQuery) as EntityId,
              (mooringId ?? position.mooringId) as EntityId,
              positionId,
            ),
          )
      }
    },
    [navigate, customerId, id, localityId, localityIdFromQuery, mooringId, position.mooringId, onPositionSelect],
  )

  const latLongs = [] as LatLngExpression[]

  switch (position.type) {
    case IPositionType.FrameLines:
      return (
        <FrameLine
          position={position}
          navigateToPosition={navigateToPosition}
          showCriticalPositions={showCriticalPositions}
        />
      )
    case IPositionType.Bridle:
      if (isFleet) {
        return (
          <InnerMooringLine
            position={position}
            navigateToPosition={navigateToPosition}
            showCriticalPositions={showCriticalPositions}
          />
        )
      }
      return (
        <Bridle
          position={position}
          navigateToPosition={navigateToPosition}
          showCriticalPositions={showCriticalPositions}
        />
      )
    case IPositionType.MooringLine:
      return (
        <MooringLine
          position={position}
          navigateToPosition={navigateToPosition}
          showCriticalPositions={showCriticalPositions}
        />
      )
    case IPositionType.Cage:
      return (
        <Cage
          position={position}
          navigateToPosition={navigateToPosition}
          steelFrame={steelFrame}
          showCageStatusPopOver={showCageStatusPopOver}
          mooringType={mooringType}
          showCriticalPositions={showCriticalPositions}
          isSmallMapView={isSmallMapView}
        />
      )
    case IPositionType.Corner:
      return (
        <Corner
          position={position}
          navigateToPosition={navigateToPosition}
          size={isFleet ? 2 : defaultRadiusSize}
          showCriticalPositions={showCriticalPositions}
        />
      )
    case IPositionType.Polygon:
      if (hasRenderableGeometry(position.geometry, position.type)) {
        return (
          <GeometryPosition
            position={position}
            navigateToPosition={onPositionSelect ? navigateToPosition : undefined}
          />
        )
      }
      position.connectedPositions.forEach(function (position) {
        latLongs.push([position.latitude, position.longitude])
      })
      return (
        <Polygon
          positions={latLongs}
          pathOptions={{
            fillOpacity: 1,
            color: 'red',
            weight: position.isCritical && shouldHighlightCritical ? 4 : 3,
            pane: 'polygon',
          }}
        />
      )
    case IPositionType.ElectricityLine:
      if (hasRenderableGeometry(position.geometry, position.type)) {
        return (
          <GeometryPosition
            position={position}
            navigateToPosition={onPositionSelect ? navigateToPosition : undefined}
          />
        )
      }
      return <></>
    default:
      return <></>
  }
}

export const CreateLayerGroup: React.FC<{
  positions?: IPosition[]
  moorings?: IMooring[]
  localities?: ILocality[]
  type?: IPositionType
  mapPosition: L.LatLngExpression
  isFleet?: boolean
  steelFrame?: boolean
  isModalView: boolean
  mooringType?: IMooringType
  showCageStatusPopOver?: boolean
  clusters?: ICluster[]
  projects?: IProjectInfo[]
  onPositionSelect?: (positionId: number, positionName: string) => void
  showCriticalPositions?: boolean
  isSmallMapView?: boolean
}> = ({
  positions,
  type,
  clusters,
  mapPosition,
  isFleet,
  steelFrame,
  isModalView,
  mooringType,
  moorings,
  localities,
  showCageStatusPopOver,
  onPositionSelect,
  projects,
  showCriticalPositions,
  isSmallMapView,
}) => {
  const filteredPositions = useMemo(
    () => positions?.filter(p => type == undefined || p.type === type),
    [positions, type],
  )

  return (
    <>
      <LayerGroup>
        {filteredPositions && (
          <CageLabelFontSizeProvider positions={filteredPositions}>
            {filteredPositions.map((p, index) => {
              return (
                <ComponentLayer
                  key={index}
                  position={p}
                  isFleet={isFleet}
                  steelFrame={steelFrame as boolean}
                  isModalView={isModalView}
                  showCageStatusPopOver={showCageStatusPopOver}
                  onPositionSelect={onPositionSelect}
                  mooringType={mooringType}
                  showCriticalPositions={showCriticalPositions}
                  isSmallMapView={isSmallMapView}
                />
              )
            })}
          </CageLabelFontSizeProvider>
        )}
        {moorings &&
          moorings.map((mooring, index) => {
            return (
              <CreateMooring
                mooring={mooring as IMooring}
                mapPosition={mapPosition}
                key={index}
                clusters={clusters}
                isModalView={isModalView}
                isFleet={mooring.type == IMooringType.Barge}
                showCageStatusPopOver={showCageStatusPopOver}
                onPositionSelect={onPositionSelect}
                showCriticalPositions={showCriticalPositions}
              />
            )
          })}
        {projects &&
          projects.map((project, index) => {
            return (
              <CreateProject
                project={project as IProjectInfo}
                mapPosition={mapPosition}
                key={index}
                clusters={clusters}
                isModalView={isModalView}
                isFleet={project.type == IMooringType.Barge}
                showCageStatusPopOver={showCageStatusPopOver}
                onPositionSelect={onPositionSelect}
              />
            )
          })}
        {localities &&
          localities
            .filter(x => x.moorings != null)
            .map(x => x.moorings)
            .forEach(x =>
              x.map((mooring, index) => {
                return (
                  <CreateMooring
                    mooring={mooring as IMooring}
                    mapPosition={mapPosition}
                    key={index}
                    isModalView={isModalView}
                    isFleet={mooring.type == IMooringType.Barge}
                    showCageStatusPopOver={showCageStatusPopOver}
                    onPositionSelect={onPositionSelect}
                    showCriticalPositions={showCriticalPositions}
                    clusters={clusters}
                  />
                )
              }),
            )}
        {positions && mooringType == IMooringType.Barge && (type == undefined || type == IPositionType.Corner) && (
          <>
            {positions.filter(p => p.isTransparent).length > 0 && (
              <Fleet positions={positions.filter(p => p.isTransparent)} />
            )}
            {positions.filter(p => !p.isTransparent).length > 0 && (
              <Fleet positions={positions.filter(p => !p.isTransparent)} />
            )}
          </>
        )}
      </LayerGroup>
    </>
  )
}

const ComponentLayerWithNavigation: React.FC<{
  position: IPosition
  isFleet?: boolean
  steelFrame: boolean
  showCageStatusPopOver?: boolean
  onPositionSelect?: (positionId: number, positionName: string) => void
  mooringType?: IMooringType
  showCriticalPositions?: boolean
  isSmallMapView?: boolean
}> = ({
  position,
  isFleet = false,
  steelFrame,
  showCageStatusPopOver,
  onPositionSelect,
  mooringType,
  showCriticalPositions,
  isSmallMapView,
}) => {
  const navigate = useNavigate()
  return (
    <GenericComponentLayer
      position={position}
      isFleet={isFleet}
      steelFrame={steelFrame}
      navigate={navigate}
      showCageStatusPopOver={showCageStatusPopOver}
      onPositionSelect={onPositionSelect}
      mooringType={mooringType}
      showCriticalPositions={showCriticalPositions}
      isSmallMapView={isSmallMapView}
    />
  )
}

export const ComponentLayer: React.FC<{
  position: IPosition
  isFleet?: boolean
  steelFrame: boolean
  isModalView: boolean
  showCageStatusPopOver?: boolean
  mooringType?: IMooringType
  onPositionSelect?: (positionId: number, positionName: string) => void
  showCriticalPositions?: boolean
  isSmallMapView?: boolean
}> = ({
  position,
  isFleet = false,
  steelFrame,
  isModalView,
  showCageStatusPopOver,
  onPositionSelect,
  mooringType,
  showCriticalPositions,
  isSmallMapView,
}) => {
  return isModalView ? (
    <GenericComponentLayer
      position={position}
      isFleet={isFleet}
      steelFrame={steelFrame}
      showCageStatusPopOver={showCageStatusPopOver}
      onPositionSelect={onPositionSelect}
      mooringType={mooringType}
      showCriticalPositions={showCriticalPositions}
      isSmallMapView={isSmallMapView}
    />
  ) : (
    <ComponentLayerWithNavigation
      position={position}
      isFleet={isFleet}
      steelFrame={steelFrame}
      showCageStatusPopOver={showCageStatusPopOver}
      onPositionSelect={onPositionSelect}
      mooringType={mooringType}
      showCriticalPositions={showCriticalPositions}
      isSmallMapView={isSmallMapView}
    />
  )
}
